import {
  AlertCircle,
  BadgeCheck,
  Calendar as CalendarIcon,
  DollarSign,
  FileDown,
  Filter,
  Loader2,
  Percent,
  PieChart,
  RefreshCcw,
  Search as SearchIcon,
  User as UserIcon,
} from 'lucide-react'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import React, { useEffect, useMemo, useState } from 'react'
import Navbar from '../../components/navbar/navbar'
import apinode from '../../services/apinode'
import { adaptValue } from '../../utils/adaptValue'

// Se precisar dos fonts no PDF, descomente a linha abaixo
// pdfMake.vfs = pdfFonts.pdfMake.vfs;

const styles = {
  page: {
    maxWidth: 1200,
    margin: '24px auto',
    padding: '0 16px',
  },
  header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 },
  headerTitle: { fontSize: 24, fontWeight: 700, margin: 0 },
  subTitle: { color: '#64748b', marginTop: 0 },
  toolbar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 12,
    marginTop: 12,
    alignItems: 'end',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: '#334155',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  input: {
    height: 40,
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    padding: '0 12px',
    outline: 'none',
  },
  select: {
    height: 40,
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    padding: '0 12px',
    outline: 'none',
    background: 'white',
  },
  btnPrimary: {
    height: 40,
    borderRadius: 10,
    border: 'none',
    padding: '0 12px',
    background: '#111827',
    color: 'white',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'center',
    cursor: 'pointer',
  },
  btnGhost: {
    height: 40,
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    padding: '0 12px',
    background: 'white',
    color: '#0f172a',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'center',
    cursor: 'pointer',
  },
  toolbarActions: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    gridColumn: '1 / -1',
  },
  cardsGrid: {
    marginTop: 16,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
    gap: 12,
  },
  card: {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    padding: 16,
  },
  cardTitle: { fontSize: 12, color: '#64748b', marginBottom: 6 },
  cardValue: { fontSize: 22, fontWeight: 800 },
  subtle: { fontSize: 11, color: '#64748b' },
  tableWrap: {
    marginTop: 16,
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    overflow: 'hidden',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  theadTh: {
    textAlign: 'left',
    fontSize: 12,
    padding: '10px 12px',
    borderBottom: '1px solid #e2e8f0',
    background: '#f8fafc',
    color: '#334155',
    fontWeight: 700,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: 13,
    color: '#0f172a',
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
  },
  statusBadge: (bg, color, border) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 999,
    background: bg,
    color,
    border: `1px solid ${border}`,
    fontSize: 12,
    fontWeight: 600,
  }),
  footer: { marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: 999,
    background: 'white',
    fontSize: 12,
    cursor: 'pointer',
  },
  error: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    padding: 12,
    background: '#fef2f2',
    color: '#b91c1c',
    border: '1px solid #fecaca',
    borderRadius: 12,
    marginTop: 12,
  },
}

function formatCurrency(v) {
  return (v || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })
}

function startEndOfThisMonth() {
  const hoje = new Date()
  const first = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
    .toISOString()
    .split('T')[0]
  const last = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)
    .toISOString()
    .split('T')[0]
  return { first, last }
}

function norm(str) {
  return String(str || '')
    .trim()
    .toLowerCase()
}
function isCanceled(status) {
  const s = norm(status)
  return s.includes('cancel') // pega "Cancelado", "Cancelada", etc.
}
function statusColors(status) {
  const s = norm(status)
  if (isCanceled(s)) return ['#fee2e2', '#b91c1c', '#fecaca'] // vermelho claro
  if (s.includes('pago') || s.includes('fatur') || s.includes('conclu'))
    return ['#dcfce7', '#166534', '#bbf7d0'] // verde
  if (s.includes('pend') || s.includes('abert') || s.includes('aguard'))
    return ['#fef9c3', '#a16207', '#fde68a'] // amarelo
  if (s.includes('devol') || s.includes('estorn'))
    return ['#fce7f3', '#9d174d', '#fbcfe8'] // rosa/vinho
  if (!s) return ['#e2e8f0', '#475569', '#cbd5e1'] // neutro para N/A
  return ['#dbeafe', '#1d4ed8', '#bfdbfe'] // azul default
}

const VendasRelatorio = () => {
  const [vendas, setVendas] = useState([])
  const [vendedores, setVendedores] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingVendedores, setLoadingVendedores] = useState(false)
  const [error, setError] = useState(null)

  const [
    { dataInicio, dataFim, vendedorSelecionado, tipoVenda, statusSelecionado },
    setFilters,
  ] = useState(() => {
    const { first, last } = startEndOfThisMonth()
    return {
      dataInicio: first,
      dataFim: last,
      vendedorSelecionado: localStorage.idusuario,
      tipoVenda: '', // '' = todos
      statusSelecionado: '', // '' = todos
    }
  })

  const [comissaoPct, setComissaoPct] = useState(0) // % da comissão

  const isAdmin = String(localStorage.admin).toUpperCase() === 'SIM'

  // Distintos tipos de venda (dos dados carregados)
  const tiposVendaDisponiveis = useMemo(() => {
    const set = new Set(
      (vendas || []).map((v) => (v.tipovenda || '').trim()).filter(Boolean),
    )
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [vendas])

  // Distintos status (dos dados carregados)
  const statusDisponiveis = useMemo(() => {
    const set = new Set(
      (vendas || []).map((v) => (v.status ?? '').toString().trim() || 'N/A'),
    )
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [vendas])

  // Totais derivados (valor, ticket, por tipo e por status, cancelados, líquido, comissão)
  const totals = useMemo(() => {
    let totalValor = 0
    let totalPedidos = 0
    let canceladoValor = 0
    let canceladoQtde = 0

    const porTipo = {}
    const porStatus = {}
    let liquidoValor = 0 // exclui cancelados

    ;(vendas || []).forEach((v) => {
      const valor = Number(v.valorpedido) || 0
      const valordevolvido = Number(v.valordevolvido) || 0
      const tipo = (v.tipovenda || 'N/A').trim()
      const status = (v.status || '').toString().trim() || 'N/A'

      totalValor += valor - valordevolvido
      totalPedidos += 1
      porTipo[tipo] = (porTipo[tipo] || 0) + valor - valordevolvido
      porStatus[status] = (porStatus[status] || 0) + valor - valordevolvido

      if (isCanceled(status)) {
        canceladoValor += valor
        canceladoQtde += 1
      } else {
        liquidoValor += valor - valordevolvido
      }
    })

    const ticketMedio = totalPedidos ? totalValor / totalPedidos : 0
    return {
      totalValor,
      totalPedidos,
      ticketMedio,
      totaisPorTipo: porTipo,
      totaisPorStatus: porStatus,
      canceladoValor,
      canceladoQtde,
      liquidoValor,
      comissaoValor: liquidoValor * (Number(comissaoPct) / 100),
    }
  }, [vendas, comissaoPct])

  // Carregar vendedores
  useEffect(() => {
    const run = async () => {
      try {
        setLoadingVendedores(true)
        setError(null)
        const resp = await apinode.get(
          `vendedores/vendedor?conta=${localStorage.idconta}`,
        )
        const lista = resp?.data || []
        setVendedores(lista)
        if (!isAdmin) {
          setFilters((prev) => ({
            ...prev,
            vendedorSelecionado: localStorage.idusuario || '',
          }))
        }
      } catch (e) {
        setError(e?.message || 'Falha ao carregar vendedores')
      } finally {
        setLoadingVendedores(false)
      }
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Carregar vendas quando filtros mudarem
  useEffect(() => {
    const fetchVendas = async () => {
      if (!dataInicio || !dataFim || !localStorage.idconta) return
      if (!isAdmin && !localStorage.idusuario) return

      setLoading(true)
      setError(null)
      try {
        const params = {
          idvendedor:
            vendedorSelecionado || (isAdmin ? '' : localStorage.idusuario),
          dataIn: dataInicio,
          dataFn: dataFim,
        }
        if (tipoVenda && tipoVenda.trim()) params.tipovenda = tipoVenda.trim()
        if (statusSelecionado && statusSelecionado.trim())
          params.status = statusSelecionado.trim()

        const url = `/relatorios/90dias/vendedor/${localStorage.idconta}`
        const resp = await apinode.get(url, { params })
        const rows = Array.isArray(resp?.data) ? resp.data : []

        // filtro no cliente (reforço)
        let filtered = rows
        if (tipoVenda) {
          filtered = filtered.filter(
            (r) => norm(r.tipovenda) === norm(tipoVenda),
          )
        }
        if (statusSelecionado) {
          if (statusSelecionado === 'N/A') {
            filtered = filtered.filter((r) => !norm(r.status))
          } else {
            filtered = filtered.filter(
              (r) => norm(r.status) === norm(statusSelecionado),
            )
          }
        }
        setVendas(filtered)
      } catch (e) {
        setError(e?.message || 'Falha ao carregar vendas')
      } finally {
        setLoading(false)
      }
    }
    fetchVendas()
  }, [
    dataInicio,
    dataFim,
    vendedorSelecionado,
    tipoVenda,
    statusSelecionado,
    isAdmin,
  ])

  const {
    totalValor,
    totalPedidos,
    ticketMedio,
    totaisPorTipo,
    totaisPorStatus,
    canceladoValor,
    canceladoQtde,
    liquidoValor,
    comissaoValor,
  } = totals

  const onGerarPDF = () => {
    const vendedorNome =
      vendedores.find(
        (v) => String(v.COD_PESSOA) === String(vendedorSelecionado),
      )?.PES_RAZAO ||
      vendas[0]?.vendedor ||
      (isAdmin ? 'Todos' : localStorage.username || 'N/A')

    // Agrupar por Tipo de Venda
    const porTipo = {}
    vendas.forEach((v) => {
      const key = (v.tipovenda || 'N/A').trim()
      if (!porTipo[key]) porTipo[key] = []
      porTipo[key].push(v)
    })

    const summaryTipoRows = Object.entries(totaisPorTipo)
      .sort((a, b) => b[1] - a[1])
      .map(([k, val]) => [k, formatCurrency(val)])

    const summaryStatusRows = Object.entries(totaisPorStatus)
      .sort((a, b) => b[1] - a[1])
      .map(([k, val]) => [
        k,
        formatCurrency(val),
        (vendas || []).filter(
          (v) => (v.status || 'N/A').toString().trim() === k,
        ).length,
      ])

    // Cabeçalho + Detalhes (agora com STATUS)
    const tabelaCorpo = [
      [
        { text: 'Doc. Vend.', style: 'tHeader', alignment: 'center' },
        { text: 'Vendedor', style: 'tHeader', alignment: 'center' },
        { text: 'Pedido', style: 'tHeader', alignment: 'center' },
        { text: 'CPF/CNPJ', style: 'tHeader', alignment: 'center' },
        { text: 'Valor (R$)', style: 'tHeader', alignment: 'right' },
        { text: 'Valor Pag. Haver (R$)', style: 'tHeader', alignment: 'right' },
        { text: 'Valor Total (R$)', style: 'tHeader', alignment: 'right' },
        { text: 'Cliente', style: 'tHeader', alignment: 'left' },
        { text: 'Data do Pedido', style: 'tHeader', alignment: 'center' },
        { text: 'Tipo', style: 'tHeader', alignment: 'center' },
        { text: 'Status', style: 'tHeader', alignment: 'center' },
      ],
    ]

    Object.keys(porTipo)
      .sort((a, b) => a.localeCompare(b))
      .forEach((tipo) => {
        const grupo = porTipo[tipo]
        const subtotal = grupo.reduce(
          (acc, v) =>
            acc + (Number(v.valorpedido) - Number(v.valordevolvido) || 0),
          0,
        )

        // Cabeçalho do grupo com 11 colunas
        tabelaCorpo.push([
          {
            text: `— ${tipo}`,
            colSpan: 11,
            style: 'groupHeader',
            alignment: 'left',
          },
          {},
          {},
          {},
          {},
          {},
          {},
          {},
          {},
          {},
          {},
        ])

        grupo.forEach((v) => {
          tabelaCorpo.push([
            {
              text: String(v.docvendedor ?? ''),
              alignment: 'center',
              fontSize: 8,
            },
            {
              text: String(v.vendedor ?? ''),
              alignment: 'center',
              fontSize: 8,
            },
            {
              text: String(v.numeropedido ?? ''),
              alignment: 'center',
              fontSize: 8,
            },
            {
              text: String(v.cpfcliente ?? ''),
              alignment: 'center',
              fontSize: 8,
            },
            {
              text: formatCurrency(v.valorpedido),
              alignment: 'right',
              fontSize: 8,
            },
            {
              text: formatCurrency(v.valordevolvido),
              alignment: 'right',
              fontSize: 8,
            },
            {
              text: formatCurrency(
                adaptValue(v.valorpedido) - adaptValue(v.valordevolvido),
              ),
              alignment: 'right',
              fontSize: 8,
            },
            { text: String(v.cliente ?? ''), alignment: 'left', fontSize: 8 },
            {
              text: v.datapedido
                ? new Date(v.datapedido).toLocaleDateString()
                : '',
              alignment: 'center',
              fontSize: 8,
            },
            {
              text: String(v.tipovenda ?? 'N/A'),
              alignment: 'center',
              fontSize: 8,
            },
            {
              text: String((v.status || '').toString().trim() || 'N/A'),
              alignment: 'center',
              fontSize: 8,
            },
          ])
        })

        // Subtotal com 11 colunas
        tabelaCorpo.push([
          {
            text: `Subtotal ${tipo}`,
            colSpan: 4,
            alignment: 'right',
            bold: true,
            fontSize: 9,
          },
          {},
          {},
          {},
          {
            text: formatCurrency(subtotal),
            alignment: 'right',
            bold: true,
            fontSize: 9,
          },
          {},
          {},
          {},
          {},
          {},
          {},
        ])
      })

    const dataHoraImpressao = new Date().toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour12: false,
    })

    const docDefinition = {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [28, 28, 28, 32],
      content: [
        { text: 'Relatório de Vendas por Vendedor', style: 'title' },
        {
          columns: [
            { text: `Vendedor: ${vendedorNome}`, style: 'sub' },
            {
              text: `Período: ${dataInicio} a ${dataFim}`,
              style: 'sub',
              alignment: 'center',
            },
            {
              text: `Impresso em: ${dataHoraImpressao}`,
              style: 'sub',
              alignment: 'right',
            },
          ],
          margin: [0, 0, 0, 8],
        },

        // KPIs: agora com Líquido e Comissão
        {
          table: {
            widths: ['*', '*', '*', '*', '*'],
            body: [
              [
                { text: 'Total de Vendas', style: 'kpiTitle' },
                { text: 'Qtde de Pedidos', style: 'kpiTitle' },
                { text: 'Ticket Médio', style: 'kpiTitle' },
                { text: 'Valor Líquido', style: 'kpiTitle' },
                {
                  text: `Comissão (${Number(comissaoPct) || 0}%)`,
                  style: 'kpiTitle',
                },
              ],
              [
                { text: formatCurrency(totalValor), style: 'kpiValue' },
                { text: String(totalPedidos), style: 'kpiValue' },
                { text: formatCurrency(ticketMedio), style: 'kpiValue' },
                { text: formatCurrency(liquidoValor), style: 'kpiValue' },
                { text: formatCurrency(comissaoValor), style: 'kpiValue' },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 10],
        },

        // Totais por Tipo
        { text: 'Totais por Tipo de Venda', style: 'section' },
        {
          table: {
            widths: ['*', 120],
            body: [
              [
                { text: 'Tipo', style: 'tHeader' },
                { text: 'Total (R$)', style: 'tHeader', alignment: 'right' },
              ],
              ...summaryTipoRows.map(([tipo, val]) => [
                { text: tipo, fontSize: 9 },
                { text: val, alignment: 'right', fontSize: 9 },
              ]),
            ],
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 10],
        },

        // Totais por Status
        { text: 'Totais por Status', style: 'section' },
        {
          table: {
            widths: ['*', 120, 60],
            body: [
              [
                { text: 'Status', style: 'tHeader' },
                { text: 'Total (R$)', style: 'tHeader', alignment: 'right' },
                { text: 'Qtde', style: 'tHeader', alignment: 'center' },
              ],
              ...summaryStatusRows.map(([st, val, q]) => [
                { text: st, fontSize: 9 },
                { text: val, alignment: 'right', fontSize: 9 },
                { text: String(q), alignment: 'center', fontSize: 9 },
              ]),
            ],
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 10],
        },

        // Detalhes - CORRIGIDO: widths com 11 colunas
        { text: 'Detalhamento de Pedidos', style: 'section' },
        {
          table: {
            headerRows: 1,
            widths: [55, 80, 55, 75, 75, 75, 75, '*', 70, 55, 55], // 11 colunas
            body: tabelaCorpo,
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0,
            hLineColor: () => '#e5e7eb',
            paddingLeft: () => 4,
            paddingRight: () => 4,
            paddingTop: () => 3,
            paddingBottom: () => 3,
          },
        },
        {
          text: `Total Cancelado: ${formatCurrency(canceladoValor)} (${canceladoQtde} pedidos)`,
          style: 'sub',
          margin: [0, 6, 0, 0],
        },
        {
          text: `Total Geral: ${formatCurrency(totalValor)}`,
          style: 'totalGeral',
          alignment: 'right',
          margin: [0, 8, 0, 0],
        },
      ],
      styles: {
        title: { fontSize: 16, bold: true, margin: [0, 0, 0, 6] },
        sub: { fontSize: 9 },
        kpiTitle: { fontSize: 9, bold: true, alignment: 'center' },
        kpiValue: { fontSize: 12, bold: true, alignment: 'center' },
        section: { fontSize: 12, bold: true, margin: [0, 10, 0, 6] },
        tHeader: { fontSize: 9, bold: true, color: 'black' },
        groupHeader: {
          fontSize: 9,
          bold: true,
          color: '#0f172a',
          fillColor: '#f8fafc',
          margin: [0, 4, 0, 2],
        },
        totalGeral: { fontSize: 11, bold: true },
      },
      defaultStyle: { fontSize: 9 },
    }

    pdfMake.createPdf(docDefinition).open()
  }

  const onReset = () => {
    const { first, last } = startEndOfThisMonth()
    setFilters((prev) => ({
      ...prev,
      dataInicio: first,
      dataFim: last,
      vendedorSelecionado: isAdmin ? '' : localStorage.idusuario || '',
      tipoVenda: '',
      statusSelecionado: '',
    }))
    setComissaoPct(0)
  }

  const userId = String(localStorage.idusuario || '')
  const vendedorNomeAtual = useMemo(() => {
    return (
      vendedores.find((v) => String(v.COD_PESSOA) === userId)?.PES_RAZAO ||
      localStorage.username ||
      'Seu usuário'
    )
  }, [vendedores, userId])

  // garante o id do vendedor quando não for admin
  useEffect(() => {
    if (!isAdmin) {
      setFilters((prev) => ({ ...prev, vendedorSelecionado: userId }))
    }
  }, [isAdmin, userId, setFilters])

  return (
    <>
      <Navbar />
      <div className="container-fluid mt-page form-pedido-editar">
        <div style={styles.header}>
          <PieChart size={24} />
          <h1 style={styles.headerTitle}>Relatório de Vendas por Vendedor</h1>
        </div>
        <p style={styles.subTitle}>
          Filtro por período, vendedor, <strong>tipo de venda</strong> e{' '}
          <strong>status</strong>. Exporte em PDF com resumo, subtotais,{' '}
          <em>valor líquido</em> e <em>comissão</em>.
        </p>

        {error && (
          <div style={styles.error}>
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Toolbar de filtros */}
        <div style={styles.toolbar}>
          <div style={styles.field}>
            <label style={styles.label}>
              <UserIcon size={14} /> Selecionar Vendedor
            </label>
            <select
              style={styles.select}
              value={vendedorSelecionado}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  vendedorSelecionado: e.target.value,
                }))
              }
              disabled={loadingVendedores} // só desabilita se estiver carregando
            >
              <option value="">Todos / Selecione</option>
              {vendedores.map((v) => (
                <option
                  key={v.COD_PESSOA}
                  value={v.COD_PESSOA}
                >
                  {v.PES_RAZAO}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>
              <CalendarIcon size={14} /> Data Inicial
            </label>
            <input
              type="date"
              style={styles.input}
              value={dataInicio}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, dataInicio: e.target.value }))
              }
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>
              <CalendarIcon size={14} /> Data Final
            </label>
            <input
              type="date"
              style={styles.input}
              value={dataFim}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, dataFim: e.target.value }))
              }
            />
          </div>

          {/* Tipo de venda como SELECT */}
          <div style={styles.field}>
            <label style={styles.label}>
              <SearchIcon size={14} /> Tipo de Venda
            </label>
            <select
              style={styles.select}
              value={tipoVenda}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, tipoVenda: e.target.value }))
              }
            >
              <option value="">Todos</option>
              {tiposVendaDisponiveis.map((tv) => (
                <option
                  key={tv}
                  value={tv}
                >
                  {tv}
                </option>
              ))}
            </select>
          </div>

          {/* Status como SELECT */}
          <div style={styles.field}>
            <label style={styles.label}>
              <BadgeCheck size={14} /> Status
            </label>
            <select
              style={styles.select}
              value={statusSelecionado}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  statusSelecionado: e.target.value,
                }))
              }
            >
              <option value="">Todos</option>
              {statusDisponiveis.map((st) => (
                <option
                  key={st}
                  value={st}
                >
                  {st}
                </option>
              ))}
            </select>
          </div>

          {/* Comissão (%) */}
          <div style={styles.field}>
            <label style={styles.label}>
              <Percent size={14} /> Comissão (%)
            </label>
            <input
              type="number"
              min="0"
              step="0.1"
              style={styles.input}
              placeholder="Ex.: 3.5"
              value={comissaoPct}
              onChange={(e) => {
                const v = e.target.value
                const n = Number(v)
                setComissaoPct(isNaN(n) ? 0 : Math.max(0, n))
              }}
            />
          </div>

          {/* Ações responsivas: ocupa a linha toda */}
          <div
            style={styles.toolbarActions}
            className="actions"
          >
            <button
              type="button"
              className="btn"
              style={styles.btnPrimary}
              onClick={() => {}}
              disabled={loading}
              title="Aplicar filtros"
            >
              {loading ? (
                <Loader2
                  size={16}
                  className="spin"
                />
              ) : (
                <Filter size={16} />
              )}
              {loading ? 'Carregando...' : 'Filtrar'}
            </button>

            <button
              type="button"
              className="btn"
              style={styles.btnGhost}
              onClick={onReset}
              title="Limpar filtros"
              disabled={loading}
            >
              <RefreshCcw size={16} />
              Limpar
            </button>

            <button
              type="button"
              className="btn"
              style={styles.btnGhost}
              onClick={onGerarPDF}
              title="Gerar PDF"
              disabled={loading || vendas.length === 0}
            >
              <FileDown size={16} />
              PDF
            </button>
          </div>
        </div>

        {/* Cards de totais */}
        <div style={styles.cardsGrid}>
          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <DollarSign size={18} />
              <div>
                <div style={styles.cardTitle}>Total de Vendas</div>
                <div style={styles.cardValue}>{formatCurrency(totalValor)}</div>
                <div style={styles.subtle}>Inclui todos os status</div>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <PieChart size={18} />
              <div>
                <div style={styles.cardTitle}>Qtde de Pedidos</div>
                <div style={styles.cardValue}>{totalPedidos}</div>
                <div style={styles.subtle}>
                  Ticket médio {formatCurrency(ticketMedio)}
                </div>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <DollarSign size={18} />
              <div>
                <div style={styles.cardTitle}>Total Cancelado</div>
                <div style={styles.cardValue}>
                  {formatCurrency(canceladoValor)}
                </div>
                <div style={styles.subtle}>{canceladoQtde} pedidos</div>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <DollarSign size={18} />
              <div>
                <div style={styles.cardTitle}>Valor Líquido</div>
                <div style={styles.cardValue}>
                  {formatCurrency(liquidoValor)}
                </div>
                <div style={styles.subtle}>Exclui cancelados</div>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Percent size={18} />
              <div>
                <div style={styles.cardTitle}>Comissão</div>
                <div style={styles.cardValue}>
                  {formatCurrency(comissaoValor)}
                </div>
                <div style={styles.subtle}>
                  Base: líquido · {Number(comissaoPct) || 0}%
                </div>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Filter size={18} />
              <div>
                <div style={styles.cardTitle}>Tipos encontrados</div>
                <div style={styles.cardValue}>
                  {tiposVendaDisponiveis.length}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div style={styles.tableWrap}>
          <table
            style={styles.table}
            className="report-table"
          >
            <thead>
              <tr>
                <th style={styles.theadTh}>Doc. Vend.</th>
                <th style={styles.theadTh}>Vendedor</th>
                <th style={styles.theadTh}>Pedido</th>
                <th style={styles.theadTh}>CPF/CNPJ</th>
                <th style={{ ...styles.theadTh, textAlign: 'right' }}>
                  Valor (R$)
                </th>
                <th style={{ ...styles.theadTh, textAlign: 'right' }}>
                  Valor Pag. Haver (R$)
                </th>

                <th style={{ ...styles.theadTh, textAlign: 'right' }}>
                  Valor Total (R$)
                </th>
                <th style={styles.theadTh}>Cliente</th>
                <th style={{ ...styles.theadTh, width: 120 }}>
                  Data do Pedido
                </th>
                <th style={styles.theadTh}>Tipo</th>
                <th style={styles.theadTh}>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={9}
                    style={styles.empty}
                  >
                    <Loader2
                      size={16}
                      className="spin"
                    />
                    Carregando vendas...
                  </td>
                </tr>
              )}

              {!loading && vendas.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    style={styles.empty}
                  >
                    <AlertCircle size={16} /> Nenhum registro encontrado para os
                    filtros atuais.
                  </td>
                </tr>
              )}

              {!loading &&
                vendas.map((v, idx) => {
                  const [bg, color, border] = statusColors(v.status)
                  return (
                    <tr key={`${v.numeropedido}-${idx}`}>
                      <td style={styles.td}>{v.docvendedor}</td>
                      <td style={styles.td}>{v.vendedor}</td>
                      <td style={styles.td}>{v.numeropedido}</td>
                      <td style={styles.td}>{v.cpfcliente}</td>
                      <td
                        style={{
                          ...styles.td,
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatCurrency(v.valorpedido)}
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                          color:
                            adaptValue(v?.valordevolvido) !== 0
                              ? 'red'
                              : 'inherit',
                        }}
                      >
                        {formatCurrency(adaptValue(-v?.valordevolvido))}
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {v?.valordevolvido && v?.valordevolvido > 0
                          ? formatCurrency(
                              adaptValue(v.valorpedido) -
                                adaptValue(v.valordevolvido),
                            )
                          : formatCurrency(v.valorpedido)}
                      </td>
                      <td style={styles.td}>{v.cliente}</td>
                      <td style={styles.td}>
                        {v.datapedido
                          ? new Date(v.datapedido).toLocaleDateString()
                          : ''}
                      </td>
                      <td style={styles.td}>{v.tipovenda || 'N/A'}</td>
                      <td style={styles.td}>
                        <span style={styles.statusBadge(bg, color, border)}>
                          {v.status?.toString().trim() || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>

        {/* Rodapé com totais por tipo (chips clicáveis) */}
        <div style={styles.footer}>
          {Object.entries(totaisPorTipo)
            .sort((a, b) => b[1] - a[1])
            .map(([tipo, val]) => (
              <button
                key={`tipo-${tipo}`}
                style={styles.chip}
                title={`Filtrar por ${tipo}`}
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    tipoVenda: tipo === 'N/A' ? '' : tipo,
                  }))
                }
              >
                <Filter size={14} />
                <strong>{tipo}:</strong> {formatCurrency(val)}
              </button>
            ))}
        </div>

        {/* Rodapé com totais por STATUS (chips clicáveis) */}
        <div style={{ ...styles.footer, marginTop: 8 }}>
          {Object.entries(totaisPorStatus)
            .sort((a, b) => b[1] - a[1])
            .map(([st, val]) => {
              const [bg, color, border] = statusColors(st)
              const qt = (vendas || []).filter(
                (v) => (v.status || 'N/A').toString().trim() === st,
              ).length
              return (
                <button
                  key={`status-${st}`}
                  style={{
                    ...styles.chip,
                    background: bg,
                    color,
                    border: `1px solid ${border}`,
                  }}
                  title={`Filtrar por status ${st}`}
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, statusSelecionado: st }))
                  }
                >
                  <BadgeCheck size={14} />
                  <strong>{st}:</strong> {formatCurrency(val)}{' '}
                  <span style={{ marginLeft: 6, opacity: 0.8 }}>({qt})</span>
                </button>
              )
            })}
        </div>
      </div>

      {/* CSS para responsividade e micro animações */}
      <style>{`
        .spin { animation: spin 0.9s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        /* Botões responsivos */
        .actions .btn { flex: 0 1 auto; }
        @media (max-width: 900px) {
          .actions .btn { flex: 1 1 calc(50% - 8px); } /* 2 por linha */
        }
        @media (max-width: 520px) {
          .actions .btn { flex: 1 1 100%; } /* 1 por linha */
        }

        /* Tabela: zebra + hover */
        .report-table tbody tr:nth-child(even) td {
          background: #fafafa;
        }
        .report-table tbody tr:hover td {
          background: #f1f5f9;
        }
      `}</style>
    </>
  )
}

export default VendasRelatorio
